import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { useCelebration } from "@/context/CelebrationContext";
import {
  consumePendingCelebration,
  initNativePushListeners,
  setNativePushForegroundHandler,
  syncNativePushTokenIfPermitted,
} from "@/lib/native/pushNotifications";
import { isNativeCapacitorApp } from "@/integrations/clients/carAuction/nativeAuth";

/**
 * Native-only: attach push listeners and sync FCM token when logged in.
 * OS permission is requested via Settings or NativePushOptInDialog soft prompt.
 * Foreground pushes show in-app toast; win events trigger celebration.
 */
export function useNativePushNotifications() {
  const navigate = useNavigate();
  const { user, isLoading } = useAuth();
  const { celebrate } = useCelebration();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!isNativeCapacitorApp()) return;

    setNativePushForegroundHandler((payload) => {
      const type = payload.type;
      if (type === "AUCTION_WON" || type === "PURCHASE_CONFIRMED") {
        const data = payload.data || {};
        const listingTitle =
          typeof data.listingTitle === "string" ? data.listingTitle : undefined;
        const regNumber =
          typeof data.regNumber === "string" ? data.regNumber : undefined;
        const vehicle =
          listingTitle && regNumber && !listingTitle.includes(regNumber)
            ? `${listingTitle} (${regNumber})`
            : listingTitle || regNumber;
        // Do not pass push title/body — backend texts are often Swedish-only;
        // CelebrationOverlay resolves copy from the user's i18n language.
        celebrate({
          kind: type === "AUCTION_WON" ? "auction_won" : "purchase",
          listingTitle: vehicle,
        });
        void queryClient.invalidateQueries({ queryKey: ["listing"] });
        void queryClient.invalidateQueries({ queryKey: ["listings"] });
        return;
      }
      if (payload.title || payload.body) {
        toast.message(payload.title || "Bilenia", {
          description: payload.body,
        });
      }
    });

    void initNativePushListeners((path) => {
      navigate(path);
    });

    return () => {
      setNativePushForegroundHandler(null);
    };
  }, [navigate, celebrate, queryClient]);

  useEffect(() => {
    if (!isNativeCapacitorApp() || isLoading || !user) return;
    void syncNativePushTokenIfPermitted();

    const pending = consumePendingCelebration();
    if (pending) {
      celebrate({
        kind: pending.kind,
        listingTitle: pending.listingTitle,
      });
    }
  }, [user, isLoading, celebrate]);
}
